import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShapesIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/ebook-images/25b9e3c3-dffd-4d10-9117-e7367e8ea61f/product-uploads/mcp_ad28b37f-6291-478c-a167-55bb84a0c9cc_1793711053555.webp';
export const heroGuarantees = ['Download imediato', 'Pagamento único', 'Sem subscrição', 'Garantia de 7 dias'];
export const stats = [{
  value: '240',
  label: 'Páginas em seis volumes'
}, {
  value: '6',
  label: 'Volumes digitais para professores'
}, {
  value: '100+',
  label: 'Jogos e exercícios prontos a usar'
}, {
  value: '5',
  label: 'Bónus incluídos no pack'
}, {
  value: '1',
  label: 'Sistema completo para todo o ano letivo'
}];
export const differenceStats = [{
  value: '1.º ao 9.º ano',
  label: 'Do 1.º ciclo ao 3.º ciclo do ensino básico'
}, {
  value: '6 volumes digitais',
  label: 'Um volume por tema, para todos os ciclos'
}, {
  value: 'PDF para imprimir',
  label: 'No telemóvel, no tablet ou impresso para o pavilhão'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'O domingo à noite vai-se na planificação',
  text: 'Passa horas a escrever as aulas da semana seguinte – e mesmo assim não termina.'
}, {
  title: '28 alunos, 3 níveis diferentes',
  text: 'Uns correm e saltam sem problemas, outros mal conseguem agarrar a bola – na mesma turma, na mesma aula.'
}, {
  title: 'Horas perdidas à procura de jogos',
  text: 'Os jogos da internet são soltos e não se encaixam numa planificação para o ano inteiro.'
}, {
  title: 'Chove e o recreio não dá',
  text: 'O pavilhão está ocupado ou é pequeno, o plano para o exterior já não serve – e tem de improvisar.'
}, {
  title: 'A avaliação rouba-lhe o fim de semana',
  text: 'Menções no 1.º ciclo, níveis de 1 a 5 no 2.º e 3.º ciclos, grelhas de observação – tudo escrito do zero.'
}, {
  title: 'Alunos com atestado ou menos desportivos',
  text: 'Precisa de tarefas também para quem não pode fazer esforço – e não tem tempo para as preparar.'
}];
export const afterItems = ['Planificação anual do 1.º ao 9.º ano', 'Aulas de 50 minutos, minuto a minuto', 'Fichas de trabalho em 3 níveis', 'Mais de 100 jogos e estafetas', 'Futebol, andebol, basquetebol, voleibol', 'Atletismo, ginástica, percursos', 'Saúde, dança e fair-play', 'Menções, níveis e grelhas de observação', 'Soluções para todas as fichas'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'O fim de semana vai-se na planificação',
  text: 'Também prepara ao domingo à noite as aulas de Educação Física da semana seguinte?'
}, {
  icon: FolderSearchIcon,
  title: 'Procura jogos na internet',
  text: 'Anda sempre à procura de jogos e exercícios adequados à sua turma e ao material que tem?'
}, {
  icon: LightbulbIcon,
  title: 'Precisa de um sistema, não de mais ideias',
  text: 'Tem muito material solto, mas falta-lhe um plano claro para todo o ano letivo?'
}, {
  icon: PencilRulerIcon,
  title: 'Não quer começar sempre do zero',
  text: 'Dava-lhe jeito ter aulas já feitas, fichas de trabalho e avaliações com soluções?'
}, {
  icon: ShuffleIcon,
  title: 'Alunos de níveis diferentes na mesma turma',
  text: 'Gostava que cada exercício tivesse uma versão mais fácil e outra mais difícil?'
}, {
  icon: LayoutGridIcon,
  title: 'Quer mais organização',
  text: 'Gostava de ter todas as aulas num só lugar e encontrar depressa o que precisa?'
}];

// Spine colours mirror the physical box.
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volume 1',
  level: '1.º ao 9.º ano',
  title: 'Planificação anual + 12 aulas minuto a minuto',
  tagline: 'O ano inteiro organizado, do 1.º ao 9.º ano.',
  intro: 'O primeiro volume organiza todo o ano letivo do 1.º ao 9.º ano e dá-lhe 12 aulas prontas a dar, cada uma com plano minuto a minuto e ficha de trabalho.',
  items: ['Planificação anual 1.º–2.º e 3.º–4.º ano', 'Planificação anual 5.º–6.º e 7.º–9.º ano', 'As 6 regras da aula', '12 aulas de 50 minutos', 'Deslocamentos, equilíbrio, lançar e agarrar', 'Velocidade, basquetebol, percursos', 'Rolamento, andebol, voleibol', 'Aquecimento, resistência, arbitragem', 'Grelha de observação e soluções'],
  outro: 'Abre, escolhe a aula e entra no pavilhão.',
  spine: 'bg-blue-900 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 2',
  level: '1.º ao 9.º ano',
  title: 'Jogos tradicionais, jogos de movimento e estafetas',
  tagline: 'Jogos e estafetas para qualquer turma.',
  intro: 'O segundo volume reúne jogos tradicionais portugueses, jogos de movimento e estafetas para pavilhão, recreio e espaços pequenos – cada um com regras, material, esquema e variantes em 3 níveis.',
  items: ['Jogos de aquecimento', 'Jogos de perseguição e reação', 'Jogos tradicionais', 'Estafetas e competições', 'Jogos para espaços pequenos', 'Jogos com música e ritmo', 'Jogos de cooperação', 'Fichas de trabalho em 3 níveis', 'Soluções'],
  outro: 'Tem sempre um jogo pronto.',
  spine: 'bg-cyan-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 3',
  level: '3.º ao 9.º ano',
  title: 'Jogos desportivos coletivos: futebol, andebol, basquetebol, voleibol',
  tagline: 'Gestos técnicos passo a passo e jogos reduzidos.',
  intro: 'O terceiro volume dá-lhe os jogos desportivos coletivos passo a passo: gestos técnicos, exercícios, regras simplificadas, esquemas de campo e jogos reduzidos.',
  items: ['Futebol', 'Andebol', 'Basquetebol', 'Voleibol', 'Exercícios por passos', 'Regras simplificadas', 'Esquemas de campo', 'Jogos 3×3 e 4×4', 'Fichas e soluções'],
  outro: 'Jogos desportivos coletivos, sem improviso.',
  spine: 'bg-red-500 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 4',
  level: '1.º ao 9.º ano',
  title: 'Atletismo, ginástica e aptidão física',
  tagline: 'Velocidade, resistência, força, flexibilidade.',
  intro: 'O quarto volume trabalha a aptidão física com atletismo, ginástica e percursos, com etapas de aprendizagem, ajudas e segurança em cada exercício.',
  items: ['Corrida de velocidade', 'Corrida de resistência', 'Salto em comprimento', 'Lançamentos', 'Rolamentos e equilíbrios', 'Percursos', 'Circuitos de força', 'Flexibilidade e alongamentos', 'Fichas e soluções'],
  outro: 'Progresso que os alunos conseguem medir.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 5',
  level: '1.º ao 9.º ano',
  title: 'Saúde, dança e estilos de vida ativos',
  tagline: 'Postura, higiene, alimentação, fair-play.',
  intro: 'O quinto volume liga o movimento à saúde: postura, higiene, alimentação, hidratação, sono, primeiros socorros simples, atividades rítmicas expressivas e fair-play.',
  items: ['Postura correta', 'Higiene depois do esforço', 'Alimentação e hidratação', 'Sono e descanso', 'Primeiros socorros simples', 'Dança e expressão', 'Fair-play e cooperação', 'Fichas de trabalho em 3 níveis', 'Soluções'],
  outro: 'Alunos que percebem porque se mexem.',
  spine: 'bg-violet-500 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 6',
  level: '1.º ao 9.º ano',
  title: 'Avaliação: níveis, critérios e grelhas de observação',
  tagline: 'Avaliação clara, sem fins de semana perdidos.',
  intro: 'O sexto volume organiza a avaliação: descritores para as menções, níveis de 1 a 5, provas práticas, fichas de progresso, testes curtos com soluções e autoavaliação.',
  items: ['Menções Insuficiente a Muito Bom', 'Níveis de 1 a 5', 'Provas práticas', 'Grelhas de observação', 'Fichas de progresso', 'Testes curtos A/B', 'Autoavaliação', 'Mensagens para os encarregados de educação', 'Soluções'],
  outro: 'Avalia de forma justa e rápida.',
  note: 'Material didático independente; não é um manual certificado.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}];
export const volumesIntro = {
  title: '6 volumes digitais para todo o ano letivo',
  text: 'Material completo para professores de Educação Física e professores do 1.º ciclo: seis temas do 1.º ao 9.º ano, com aulas, jogos, fichas de trabalho, avaliação e soluções. Cada volume tem 40 páginas.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Aulas minuto a minuto',
  text: 'Nunca mais parte da folha em branco. Aulas de 50 minutos com objetivo, material, segurança e as frases exatas do professor.'
}, {
  icon: FileTextIcon,
  title: 'Fichas de trabalho em 3 níveis',
  text: 'Cada ficha em três níveis – Iniciante, Avançado, Campeão – com espaço para preencher e soluções, pronta a imprimir.'
}, {
  icon: ShapesIcon,
  title: 'Esquemas de campo e de pavilhão',
  text: 'Cada jogo com o esquema visto de cima: equipas, bolas, cones e setas – percebe o jogo num relance.'
}, {
  icon: DicesIcon,
  title: 'Mais de 100 jogos',
  text: 'Jogos de movimento, jogos tradicionais, estafetas, jogos desportivos e percursos – para nenhuma aula ser aborrecida.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Avaliação já feita',
  text: 'Descritores para as menções, critérios para os níveis de 1 a 5 e grelhas de observação para preencher.'
}, {
  icon: CalendarCheckIcon,
  title: 'Plano para todo o ano letivo',
  text: 'De setembro a junho, do 1.º ao 9.º ano, com base nas Aprendizagens Essenciais de Educação Física.'
}];
export const pageSamples: Array<{src: string; label: string; title: string; text: string}> = [
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/25b9e3c3-dffd-4d10-9117-e7367e8ea61f/1793730846283_efp-v1-p05-8daf383c.webp",
    "label": "Volume 1 · página 5",
    "title": "A planificação anual",
    "text": "O ano inteiro por matérias, do 1.º ao 4.º ano: o que fazer e o que o aluno consegue."
  },
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/25b9e3c3-dffd-4d10-9117-e7367e8ea61f/1793730854466_efp-v1-p08-dd9da5ef.webp",
    "label": "Volume 1 · página 8",
    "title": "A aula minuto a minuto",
    "text": "Objetivo, material, segurança, linha do tempo de 50 minutos e as frases do professor."
  },
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/25b9e3c3-dffd-4d10-9117-e7367e8ea61f/1793730862018_efp-v1-p09-1a725321.webp",
    "label": "Volume 1 · página 9",
    "title": "Ficha de trabalho em 3 níveis",
    "text": "Iniciante, Avançado e Campeão na mesma ficha, com soluções para o professor."
  },
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/25b9e3c3-dffd-4d10-9117-e7367e8ea61f/1793730869425_efp-v1-p13-279252a5.webp",
    "label": "Volume 1 · página 13",
    "title": "Fichas com cálculo e jogo",
    "text": "O alvo, os pontos e a tabela para registar cada lançamento."
  },
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/25b9e3c3-dffd-4d10-9117-e7367e8ea61f/1793730876592_efp-v1-p20-b419cb19.webp",
    "label": "Volume 1 · página 20",
    "title": "Ginástica com segurança",
    "text": "O rolamento à frente passo a passo, com ajudas e pontos-chave."
  },
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/25b9e3c3-dffd-4d10-9117-e7367e8ea61f/1793730884588_efp-v1-p39-a1e3fd30.webp",
    "label": "Volume 1 · página 39",
    "title": "Grelha de observação",
    "text": "Menções de Muito Bom a Insuficiente, prontas a preencher."
  }
];
export const pageTypes = ['Planificação', 'Aula minuto a minuto', 'Ficha em 3 níveis', 'Grelha de observação'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Aquecimentos em 5 minutos',
  text: '40 jogos rápidos de aquecimento para o pavilhão e o recreio.',
  items: ['Jogos de apanhada', 'Mobilidade', 'Jogos com sinais', 'Aquecimentos com bola'],
  benefit: 'Todas as aulas começam com energia.'
}, {
  title: 'Dia do desporto na escola',
  text: 'Plano completo para um dia do desporto: estafetas, estações, horário e diplomas.',
  items: ['Horário do dia', 'Estafetas e estações', 'Funções para voluntários', 'Diplomas'],
  benefit: 'Organiza o dia do desporto sem stress.'
}, {
  title: 'Comunicação com os encarregados de educação',
  text: 'Mensagens prontas sobre equipamento, atestados, resultados e reuniões.',
  items: ['Início do ano', 'Equipamento desportivo', 'Atestados médicos', 'Resultados e progresso'],
  benefit: 'Comunicação clara e tranquila com as famílias.'
}, {
  title: 'Substituições de Educação Física sem stress',
  text: '10 aulas prontas para qualquer professor que o substitua.',
  items: ['Plano passo a passo', 'Material simples', 'Jogos seguros', 'Ficha para o substituto'],
  benefit: 'A aula continua mesmo sem si.'
}, {
  title: 'Cartazes para o pavilhão',
  text: 'Regras, segurança, fair-play, aquecimento e higiene – para imprimir e afixar no pavilhão.',
  items: ['Regras da aula', 'Segurança', 'Fair-play', 'Higiene'],
  benefit: 'Até as paredes do pavilhão o ajudam.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Abra EDUCAÇÃO FÍSICA PRONTA™',
  text: 'Descarregue o pack e abra o volume de que precisa.'
}, {
  title: 'Escolha a aula',
  text: 'Procure o ano na planificação anual: a aula minuto a minuto já está pronta.'
}, {
  title: 'Prepare-se em 5 minutos',
  text: 'Leia a aula, prepare o material e escolha o nível da ficha de trabalho.'
}, {
  title: 'Imprima e dê a aula',
  text: 'Imprima a ficha ou abra a aula no telemóvel – e entre no pavilhão com um plano claro.'
}];
export const audienceItems = ['Dá aulas de Educação Física do 1.º ao 9.º ano', 'É professor do 1.º ciclo e também dá Educação Física à sua turma', 'Quer ter todo o ano letivo planificado com antecedência', 'Passa os fins de semana à procura de jogos e exercícios', 'Tem na mesma turma alunos com níveis muito diferentes', 'Quer uma avaliação clara, com menções e níveis'];
export const timeValueTasks = ['Procurar jogos adequados à turma', 'Escrever o plano da aula', 'Fichas de trabalho para níveis diferentes', 'Preparar a avaliação', 'A planificação anual'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 volumes digitais – 240 páginas',
  detail: 'Planificação e aulas, jogos tradicionais e estafetas, jogos desportivos coletivos, atletismo e ginástica, saúde e avaliação'
}, {
  label: '5 bónus sem custo adicional',
  detail: 'Aquecimentos em 5 minutos, dia do desporto, comunicação com os encarregados de educação, substituições e cartazes para o pavilhão'
}, {
  label: 'Do 1.º ao 9.º ano',
  detail: 'Aulas minuto a minuto, fichas de trabalho em 3 níveis'
}];
export const offerIncludes = ['Download imediato', 'Ficheiros PDF para sempre', 'Fichas para imprimir', 'Nada enviado por correio'];
export const PRICE = '19 €';

// Only add authentic reviews of EDUCAÇÃO FÍSICA PRONTA. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Como recebo o material?',
  a: 'EDUCAÇÃO FÍSICA PRONTA™ é um produto digital. Depois da compra recebe acesso aos ficheiros PDF, que pode descarregar e guardar. Usa-os no computador, no tablet ou no telemóvel e imprime só as páginas de que precisa.'
}, {
  q: 'Para que anos é?',
  a: 'Do 1.º ao 9.º ano: 1.º ciclo (para professores titulares e professores de Educação Física) e 2.º e 3.º ciclos (para professores de Educação Física).'
}, {
  q: 'Segue as Aprendizagens Essenciais?',
  a: 'As aulas foram criadas com base nas Aprendizagens Essenciais de Educação Física (atividades físicas, aptidão física e conhecimentos). É um material didático independente, não um manual certificado – usa-o com qualquer planificação.'
}, {
  q: 'Preparo mesmo uma aula em 5 minutos?',
  a: 'EDUCAÇÃO FÍSICA PRONTA™ foi criado para encurtar a preparação. Abre o volume, escolhe a aula e lê o plano em cerca de cinco minutos. O tempo depende da turma e do que quiser adaptar.'
}, {
  q: 'O material chega para o ano inteiro?',
  a: 'Sim. A planificação anual cobre todo o ano letivo, e os seis volumes dão-lhe aulas, jogos, fichas e avaliação para cada tema.'
}, {
  q: 'Tenho alunos de níveis diferentes na turma. Como faço?',
  a: 'As fichas de trabalho têm 3 níveis: ★ Iniciante, ★★ Avançado e ★★★ Campeão – assim cada aluno trabalha ao seu nível, ao mesmo tempo.'
}, {
  q: 'Tenho um pavilhão pequeno ou não tenho pavilhão. Funciona?',
  a: 'Sim. Muitos jogos são para o recreio, para espaços pequenos ou até para a sala de aula, e cada aula diz-lhe exatamente o material de que precisa.'
}, {
  q: 'Tem avaliação com menções e níveis?',
  a: 'Sim. O Volume 6 tem descritores para as menções (Insuficiente, Suficiente, Bom, Muito Bom), critérios para os níveis de 1 a 5, provas práticas e grelhas de observação prontas a preencher.'
}, {
  q: 'Os cinco bónus estão incluídos no preço?',
  a: `Sim. Os cinco bónus estão incluídos sem custo adicional – tudo junto por ${PRICE}.`
}, {
  q: 'É uma subscrição?',
  a: 'Não. Paga uma única vez e recebe acesso aos materiais digitais. Não há mensalidade.'
}, {
  q: 'Posso guardar os ficheiros?',
  a: 'Sim. Pode descarregar e guardar os ficheiros PDF de acordo com as condições do produto.'
}, {
  q: 'Tem garantia?',
  a: 'Sim. EDUCAÇÃO FÍSICA PRONTA™ tem garantia de satisfação de 7 dias. Se o material não corresponder às suas expectativas, escreva-nos e pode pedir o reembolso dentro do prazo da garantia, de acordo com as condições.'
}];
export const footerLinks = ['Termos e condições', 'Política de privacidade', 'Política de reembolso', 'Contacto'];
